import React, { useState } from "react";

import { Button } from "@portex-pro/ui-components";
import { Container } from "api/rest/quote-requests";
import { ContainerSize } from "api/types/generated-types";
import StepCollapse from "app/pages/quote-request/_components/StepCollapse";
import { useCreateStepCollapseProps } from "app/pages/quote-request/_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "app/pages/quote-request/types";
import clsx from "clsx";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getContainerSizeLabel } from "utils/fcl-containers/getContainerSizeLabel";
import { removeFromArray } from "utils/removeFromArray";

import { FinalSchema } from "../../_schemas";
import { ContainerCard } from "./ContainerCard";

interface ContainersStepTagProps {
  containers: Container[];
}

const ContainersStepTag: React.FC<ContainersStepTagProps> = ({ containers }) => {
  const { t } = useTranslation(["quoteRequest", "common"]);

  if (!containers || containers.length === 0) {
    return null;
  }

  const firstContainer = containers[0];
  const parts = [];

  if (firstContainer.size && firstContainer.type) {
    const sizeLabel = getContainerSizeLabel(firstContainer.size as ContainerSize);
    const typeLabel = t(`common:trailerTypes.${firstContainer.type}`);
    parts.push(`${sizeLabel} ${typeLabel}`);
  } else if (firstContainer.size) {
    const sizeLabel = getContainerSizeLabel(firstContainer.size as ContainerSize);
    parts.push(`${sizeLabel}`);
  } else if (firstContainer.type) {
    const typeLabel = t(`common:trailerTypes.${firstContainer.type}`);
    parts.push(`${typeLabel}`);
  }

  const additionalInfo = [];
  if (firstContainer.weight) {
    additionalInfo.push(`${firstContainer.weight.toLocaleString()} kg`);
  }
  if (firstContainer.volume) {
    additionalInfo.push(`${firstContainer.volume} cbm`);
  }

  const displayText = parts.join("  ");

  return (
    <div className="flex items-center space-x-1 text-blue-600 text-sm">
      <span className="font-bold">{displayText}</span>
      <span>{additionalInfo.join(", ")}</span>
    </div>
  );
};

export const ContainersStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common"]);
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Containers,
    name: t("common:containers"),
    number: 3,
  });

  const [expanded, setExpanded] = useState<number | false>(0);

  const methods = useFormContext<FinalSchema>();

  const containers = useWatch({ control: methods.control, name: "containers" });

  const addContainer = () => {
    setQuoteRequestAction({ load_spec: { containers: [...containers, {}] as Container[] } });
    setExpanded(containers.length);
  };

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Shipment);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={<ContainersStepTag containers={(containers as Container[]) || []} />}
      footer={
        stepIndex <= QuoteStepIndex.Containers ? (
          <div className="flex justify-end mt-2 w-full">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("quoteRequest:RoutingStep.nextButton")}
            </Button>
          </div>
        ) : null
      }
    >
      <div>
        <div className="max-w-[572px] mt-4">
          {containers.map((_, index) => (
            <ContainerCard
              key={index}
              index={index}
              expanded={expanded === index}
              onAccordionChange={() => (expanded === index ? setExpanded(false) : setExpanded(index))}
              onRemove={() =>
                setQuoteRequestAction({ load_spec: { containers: removeFromArray(containers, index) as Container[] } })
              }
              onCopy={() =>
                setQuoteRequestAction({ load_spec: { containers: [...containers, containers[index]] as Container[] } })
              }
              canRemove={containers.length > 1}
            />
          ))}
          <div
            className={clsx(
              "flex justify-start border border-gray-200 px-4 py-2",
              containers.length === 0 ? "rounded-xl" : "rounded-b-xl"
            )}
          >
            <button
              type="button"
              onClick={addContainer}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium py-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{t("quoteRequest:ContainersStep.addAnotherContainer")}</span>
            </button>
          </div>
        </div>
      </div>
    </StepCollapse>
  );
};
